import React from 'react'
import { EligibilityCriteria } from '@/types/Event'
import { find, split, startCase } from 'lodash'
import { Tooltip } from 'flowbite-react'

const EligibilityCriteriaComponent = ({
  eligibility,
}: {
  eligibility: EligibilityCriteria[]
}) => {
  const ageRequirement = find(
    eligibility,
    (e) => e.criteria === 'age_requirement'
  )

  const profileRequirement = find(
    eligibility,
    (e) => e.criteria === 'profile_requirement'
  )

  const genderRequirement = find(
    eligibility,
    (e) => e.criteria === 'gender_requirement'
  )

  const teamSizeRequirement = find(
    eligibility,
    (e) => e.criteria === 'team_size_requirement'
  )

  const geographicRestriction = find(
    eligibility,
    (e) => e.criteria === 'geographic_restriction'
  )

  return (
    <section
      id="eligibility_criteria"
      className="w-full py-14 flex flex-col items-start gap-6"
    >
      <h3 className="text-gray-800 text-2xl font-bold">Eligibility Criteria</h3>
      <ul className="w-full flex flex-wrap md:items-center justify-around lg:justify-center text-gray-800 md:divide-x">
        {ageRequirement && (
          <li className="md:h-16 flex grow flex-col md:items-center">
            <span className="text-sm">Ages</span>
            <span className="font-semibold">
              {ageRequirement.value ? `${ageRequirement.value}+` : 'All ages'}
            </span>
          </li>
        )}
        {genderRequirement && (
          <li className="md:h-16 flex grow flex-col md:items-center">
            <span className="text-sm">Gender</span>
            {genderRequirement.value &&
              split(genderRequirement.value, '|').map((gender, index) => (
                <span className="font-semibold" key={index}>
                  {startCase(gender)}
                </span>
              ))}
            {!genderRequirement.value && (
              <span className="font-semibold">All genders</span>
            )}
          </li>
        )}
        {teamSizeRequirement && (
          <li className="md:h-16 flex grow flex-col md:items-center">
            <span className="text-sm">Teams</span>
            <div className="font-semibold">
              {split(teamSizeRequirement.value, '|')[0]}
              {split(teamSizeRequirement.value, '|')[1] &&
                ` - ${split(teamSizeRequirement.value, '|')[1]}`}
              <span> members</span>
            </div>
          </li>
        )}
        {profileRequirement && (
          <li className="md:h-16 flex grow flex-col md:items-center w-full md:w-auto py-6 md:py-0 mt-6 md:mt-0 border-t md:border-t-0">
            <span className="text-sm">Profile</span>
            <span className="font-semibold">
              {profileRequirement.value
                ? startCase(profileRequirement.value)
                : 'All profiles'}
            </span>
          </li>
        )}
        {geographicRestriction && (
          <li className="md:h-16 w-full lg:w-auto pt-6 lg:pt-0 border-t lg:border-t-0 mt-0 md:mt-6 lg:mt-0 border-l-transparent lg:border-l-gray-200 flex grow flex-col md:items-center">
            <span className="text-sm">Regions</span>
            <div className="flex flex-row gap-2 font-semibold">
              {geographicRestriction.value && (
                <>
                  {geographicRestriction.required === 'exclude_territories' && (
                    <p>
                      Exclude{' '}
                      <span className="underline">
                        specific countries/territories
                      </span>
                    </p>
                  )}
                  {geographicRestriction.required === 'include_territories' && (
                    <p>
                      Only{' '}
                      <span className="underline">
                        specific countries/territories
                      </span>
                    </p>
                  )}
                  {geographicRestriction.required === 'include_regions' && (
                    <p>
                      Only <span className="underline">specific regions</span>
                    </p>
                  )}

                  <Tooltip
                    className="max-w-[192px] w-auto z-50"
                    content={geographicRestriction.value.replaceAll('|', ', ')}
                  >
                    <svg
                      className="w-5 h-6 text-gray-400 dark:text-white hover:cursor-pointer"
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_1147_13670)">
                        <path
                          d="M8.33706 6.61114L8.7528 6.33335L8.33706 6.61114C8.55683 6.94004 8.86918 7.19638 9.23464 7.34776C9.47778 7.44847 9.73716 7.49971 9.9978 7.5H8.5C8.10218 7.5 7.72065 7.65803 7.43934 7.93934C7.15804 8.22064 7 8.60217 7 9C7 9.39782 7.15804 9.77935 7.43934 10.0607C7.72065 10.342 8.10218 10.5 8.5 10.5H9V12.5H8.5C8.10218 12.5 7.72065 12.658 7.43934 12.9393C7.15804 13.2206 7 13.6022 7 14C7 14.3978 7.15804 14.7794 7.43934 15.0607C7.72065 15.342 8.10218 15.5 8.5 15.5H12.5C12.8978 15.5 13.2794 15.342 13.5607 15.0607C13.842 14.7794 14 14.3978 14 14C14 13.6022 13.842 13.2206 13.5607 12.9393C13.2794 12.658 12.8978 12.5 12.5 12.5H12V9C12 8.60217 11.842 8.22064 11.5607 7.93934C11.2794 7.65803 10.8978 7.5 10.5 7.5H10.0022C10.1319 7.49985 10.2618 7.4871 10.3902 7.46157C10.7781 7.3844 11.1345 7.19392 11.4142 6.91421C11.6939 6.63451 11.8844 6.27814 11.9616 5.89018C12.0387 5.50222 11.9991 5.10008 11.8478 4.73463C11.6964 4.36918 11.44 4.05682 11.1111 3.83706C10.7822 3.6173 10.3956 3.5 10 3.5C9.46957 3.5 8.96086 3.71071 8.58579 4.08578C8.21072 4.46086 8 4.96957 8 5.5C8 5.89556 8.1173 6.28224 8.33706 6.61114ZM5.22209 2.10104C6.78428 1.05722 8.6209 0.500055 10.4997 0.5C13.0185 0.502837 15.4333 1.50466 17.2143 3.2857C18.9954 5.0668 19.9972 7.48169 20 10.0005C19.9999 11.8793 19.4427 13.7158 18.399 15.2779C17.3551 16.8402 15.8714 18.0578 14.1355 18.7769C12.3996 19.4959 10.4895 19.684 8.64665 19.3175C6.80383 18.9509 5.11109 18.0461 3.78249 16.7175C2.45389 15.3889 1.5491 13.6962 1.18254 11.8534C0.815985 10.0105 1.00412 8.1004 1.72315 6.36451C2.44218 4.62861 3.65982 3.14491 5.22209 2.10104Z"
                          fill="#374151"
                          stroke="#374151"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1147_13670">
                          <rect
                            width="20"
                            height="20"
                            fill="white"
                            transform="translate(0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </Tooltip>
                </>
              )}
              {!geographicRestriction.value &&
                'All countries/territories, excluding standard exceptions'}
            </div>
          </li>
        )}
      </ul>
    </section>
  )
}

export default EligibilityCriteriaComponent
